import React, { ReactNode } from 'react';

import { Control } from 'react-hook-form';
import { MultiValue, SingleValue } from 'react-select';
import AsyncCreatableSelect from 'react-select/async-creatable';
import CreatableSelect from 'react-select/creatable';

import ConditionalWrapper from 'components/ConditionalWrapper';
import SelectWrapper from 'components/SelectWrapper';
import UseFormControlWrapper from 'components/UseFormControllWrapper';
import { OptionType } from 'utils/inputSelect';

export interface SelectCreatableSyncProps {
  name: string;
  useFormControl?: boolean;
  control?: Control<any>;
  required?: boolean;
  label?: string;
  formatCreateLabel?: (inputValue: string) => ReactNode;
  onFocus?: () => void;
  onInputChange?: (input: string) => void;
  onChangeControl?: (option: string) => void;
  onChange?: (option: MultiValue<OptionType> | SingleValue<OptionType>) => void;
  value?: OptionType;
  inputValue?: string;
  isClearable?: boolean;
  isDisabled?: boolean;
  isValidNewOption?: (input: string) => boolean;
  blurInputOnSelect?: boolean;
  placeholder?: () => string | null;
  noOptionsMessage?: () => string | null;
  inputContainerClassname?: string;
  options?: OptionType[];
  errorMessage?: string;
  loading?: boolean | null;
  errorMessageReferenceName?: string;
  isOptionWithIcon?: boolean;
}

export interface SelectCreatableAsyncProps extends SelectCreatableSyncProps {
  loadOptions?: (tmp: string) => Promise<OptionType[]>;
}

type SelectCreatableProps = SelectCreatableAsyncProps | SelectCreatableSyncProps;

const isSelectCreatableAsyncProps = (props: SelectCreatableProps): props is SelectCreatableAsyncProps =>
  'loadOptions' in props;

const SelectCreatable = (props: SelectCreatableProps) => {
  const {
    useFormControl,
    control,
    name,
    required,
    errorMessageReferenceName,
    label,
    inputContainerClassname,
    errorMessage,
    loading,
    isDisabled,
    value,
    isOptionWithIcon,
  } = props;

  return (
    <ConditionalWrapper
      condition={useFormControl}
      wrapper={(children) => (
        <UseFormControlWrapper
          name={name}
          control={control}
          required={required}
          errorMessageReferenceName={errorMessageReferenceName}
        >
          {children}
        </UseFormControlWrapper>
      )}
    >
      <SelectWrapper
        label={label}
        inputContainerClassname={inputContainerClassname}
        errorMessage={errorMessage}
        loading={loading}
        isDisabled={isDisabled}
        value={value}
        showSearchIndicator
        isOptionWithIcon={isOptionWithIcon}
      >
        {isSelectCreatableAsyncProps(props) ? <AsyncCreatableSelect {...props} /> : <CreatableSelect {...props} />}
      </SelectWrapper>
    </ConditionalWrapper>
  );
};

export default SelectCreatable;
